'use client';

import classNames from 'classnames';
import type { HTMLAttributes } from 'react';
import { AiFillCloseCircle } from 'react-icons/ai';
import Select, {
  ActionMeta,
  ClearIndicatorProps,
  Props,
  components,
  type OptionProps,
  type SingleValueProps,
} from 'react-select';
import { VariantProps } from 'tailwind-variants';
import { Text } from '../../../../typography';
import { IsVisible } from '../../../../utils';
import { singleSelectStyles } from './styles';

export type ISelectFieldActionMeta = ActionMeta<any>;

function ClearIndicator(props: ClearIndicatorProps) {
  return (
    <components.ClearIndicator {...props}>
      <AiFillCloseCircle />
    </components.ClearIndicator>
  );
}

type Option = {
  label: string;
  shouldInjectAsHtml?: boolean;
  value: string | number | boolean;
};

type CustomOptionProps = OptionProps<Option>;

function Option({ data, ...props }: CustomOptionProps) {
  return (
    <components.Option data={data} {...props}>
      <span dangerouslySetInnerHTML={{ __html: data.label }} />
    </components.Option>
  );
}

function SingleValue({ data, ...props }: SingleValueProps<Option>) {
  return (
    <components.SingleValue data={data} {...props}>
      <span dangerouslySetInnerHTML={{ __html: data.label }} />
    </components.SingleValue>
  );
}

export interface ISelectFieldProps
  extends Props,
    VariantProps<typeof singleSelectStyles.container> {
  disabled?: boolean;
  error?: string;
  label?: string;
  labelProps?: {
    className?: string;
    rest?: HTMLAttributes<HTMLLabelElement>;
  };
}

export function SelectField({
  design,
  disabled,
  error,
  id,
  label,
  labelProps,
  options,
  placeholder,
  value,
  ...props
}: ISelectFieldProps) {
  return (
    <div
      className={classNames('relative', {
        'cursor-not-allowed': disabled,
      })}
    >
      <IsVisible when={!!label}>
        <Text
          className={singleSelectStyles.label({
            className: labelProps?.className,
            design,
            errored: !!error,
          })}
          variant="label"
          {...labelProps?.rest}
        >
          {label}
        </Text>
      </IsVisible>
      <Select
        classNames={{
          container: () =>
            singleSelectStyles.container({ design, errored: !!error }),
          control: ({ isFocused }) =>
            singleSelectStyles.control({
              design,
              focus: isFocused,
            }),
          placeholder: () => singleSelectStyles.placeholder({ design }),
          input: () => singleSelectStyles.input({ design }),
          valueContainer: () =>
            singleSelectStyles.valueContainer({ design, disabled }),
          singleValue: () =>
            singleSelectStyles.singleValue({ design, disabled }),
          indicatorsContainer: () =>
            singleSelectStyles.indicatorsContainer({ design }),
          clearIndicator: () => singleSelectStyles.clearIndicator({ design }),
          indicatorSeparator: () =>
            singleSelectStyles.indicatorSeparator({ design }),
          dropdownIndicator: () =>
            singleSelectStyles.dropdownIndicator({ design, error: !!error }),
          menu: () => singleSelectStyles.menu({ design }),
          groupHeading: () => singleSelectStyles.groupHeading({ design }),
          option: ({ isFocused, isSelected }) =>
            singleSelectStyles.option({
              design,
              focus: isFocused,
              selected: isSelected,
            }),
          noOptionsMessage: () =>
            singleSelectStyles.noOptionsMessage({ design }),
        }}
        components={{
          ClearIndicator,
          Option: (props: OptionProps<any>) => Option(props),
          SingleValue: (props: SingleValueProps<any>) => SingleValue(props),
        }}
        id={`select-${id}`}
        inputId={`search-input-${id}`}
        instanceId={`select-${id}`}
        isDisabled={disabled}
        isMulti={false}
        options={options}
        placeholder={placeholder || 'Select an option'}
        styles={{
          input: base => ({
            ...base,
            'input:focus': {
              boxShadow: 'none',
            },
          }),
          control: base => ({
            ...base,
            minHeight: 'auto',
          }),
        }}
        unstyled
        value={value}
        {...props}
      />
      <IsVisible when={!!error}>
        <div className="flex mt-1 ml-auto w-fit">
          <span className="text-primary-two text-xs">{error}</span>
        </div>
      </IsVisible>
    </div>
  );
}
